import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import gql from 'graphql-tag';
import { Row, Col, Tab, Tabs } from 'react-bootstrap';
import { Modal } from '@storable/canaveral-components';
import Button from '../../../../modules/react/components/stateless/Button';
import CurrentPageContext from '../../../contexts/CurrentPageContext';
import { Icon } from '../../../../src/web/components';
import Select from '../../../../modules/react/components/stateful/Select';
import StarRack from '../../../components/stateless/StarRack';
import { sortFacilities } from '../../../../src/web/utils/distanceUtils';
import FacilitySelectTable from '../../../../src/web/components/FacilitySelectTable/FacilitySelectTable';
import { getForegroundColor, getBackgroundColor } from '../../../../src/web/utils/colorSelect';
import { WHITE, MAIN, PRIMARY, SECONDARY, TERTIARY } from '../../../../src/web/utils/colorSelect/constants';
import styles from './ReviewStars.scss';
import styling from './ReviewStarsModal.scss';

const starSizes = [
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
];

const propTypes = {
  colors: PropTypes.shape({
    primary: PropTypes.string.isRequired,
    secondary: PropTypes.string.isRequired,
    tertiary: PropTypes.string.isRequired,
  }).isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onFinishEdit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  showModal: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  rating: PropTypes.number.isRequired,
  enabledFacilities: PropTypes.shape({
    id: PropTypes.bool.isRequired,
  }),
  // eslint-disable-next-line react/no-unused-prop-types
  facilities: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    pagePaths: PropTypes.arrayOf(PropTypes.shape({
      locale: PropTypes.string.isRequired,
    })),
  })),
  // eslint-disable-next-line react/no-unused-prop-types
  locale: PropTypes.string.isRequired,
  reviewCount: PropTypes.number.isRequired,
  avgRating: PropTypes.func.isRequired,
};

const defaultProps = {
  showModal: false,
  facilities: [],
  enabledFacilities: {},
};

const prepareState = ({
  facilities, starSize, starColor, rating,
  backgroundColor, fontColor, redirect, enabledFacilities, locale,
}) => {
  const state = {
    starSize,
    starColor,
    backgroundColor,
    fontColor,
    rating,
    redirect,
    liveFacilities: [],
    enabledFacilities: enabledFacilities || {},
  };

  state.liveFacilities = facilities
    .filter(facility => facility.pagePaths.some(path => path.locale === locale));

  if (Object.keys(enabledFacilities).length) {
    state.liveFacilities.forEach((facility) => {
      state.enabledFacilities[facility.id] = enabledFacilities[facility.id] || false;
    });
  } else {
    state.liveFacilities.forEach((facility) => {
      state.enabledFacilities[facility.id] = true;
    });
  }

  state.liveFacilities = sortFacilities(state.liveFacilities.filter(Boolean));

  return state;
};

class ReviewStarsModal extends React.Component {
  static contextType = CurrentPageContext;

  state = prepareState(this.props);

  handleCancelEdit = () => {
    const { onClose, onCancelEdit } = this.props;
    onClose();
    onCancelEdit();
  };

  handleRedirectChange = ({ target }) => {
    this.setState({ redirect: target.value });
  };

  handleChange = (id, { target }) => {
    const { enabledFacilities } = this.state;
    const { avgRating } = this.props;
    enabledFacilities[id] = target.checked || false;

    this.setState({
      enabledFacilities,
      rating: avgRating(),
    });
  };

  handleSelectAllChange = ({ target }) => {
    const { liveFacilities } = this.state;
    const { avgRating } = this.props;
    const enabledFacilities = {};
    liveFacilities.forEach((facility) => {
      enabledFacilities[facility.id] = target.checked;
    });

    this.setState({
      enabledFacilities,
      rating: avgRating(),
    });
  };

  save = () => {
    const {
      enabledFacilities,
      backgroundColor,
      fontColor,
      starSize,
      starColor,
      redirect,
    } = this.state;
    const { onFinishEdit } = this.props;
    const payload = {
      enabledFacilities,
      backgroundColor,
      fontColor,
      starSize,
      starColor,
      redirect,
    };
    onFinishEdit(payload);
  };

  selectStarSize = ({ value }) => {
    this.setState({ starSize: value });
  };

  selectStarColor = ({ value }) => {
    this.setState({ starColor: value });
  };

  selectFontColor = ({ value }) => {
    this.setState({ fontColor: value });
  };

  selectBackgroundColor = ({ value }) => {
    this.setState({ backgroundColor: value });
  };

  renderColor = marginTop => option => (
      <span>
        <div
          style={{ backgroundColor: option.code, marginTop }}
          className={styling.colorBlock}
        />
        {option.label}
      </span>
  )

  renderOption = () => option => (
      <span>{option.label}</span>
  );

  render() {
    const { reviewCount, showModal } = this.props;
    const { liveFacilities,
      enabledFacilities,
      redirect, backgroundColor,
      fontColor,
      rating,
      starColor,
      starSize } = this.state;
    const { primaryColor, secondaryColor, tertiaryColor } = this.context;
    const fontColors = [
      { value: WHITE, label: 'White', code: '#fff' },
      { value: MAIN, label: 'Main text color', code: '#333' },
      { value: PRIMARY, label: 'primary', code: primaryColor },
      { value: SECONDARY, label: 'secondary', code: secondaryColor },
      { value: TERTIARY, label: 'tertiary', code: tertiaryColor },
    ];
    const bgColors = [
      { value: WHITE, label: 'White', code: '#fff' },
      { value: PRIMARY, label: 'primary', code: primaryColor },
      { value: SECONDARY, label: 'secondary', code: secondaryColor },
      { value: TERTIARY, label: 'tertiary', code: tertiaryColor },
    ];
    const starColors = [
      { label: 'Default - yellow/gray', value: PRIMARY, code: primaryColor },
      { label: 'White', value: 'secondary', code: '#fff' },
    ];
    const { foregroundClass } = getForegroundColor(fontColor, this.context);
    const { backgroundClass } = getBackgroundColor(backgroundColor, this.context);

    return (
      <Modal
        show={showModal}
        onDismiss={this.handleCancelEdit}
        size="xl"
        aria-labelledby="review-stars-modal"
      >
        <Modal.Header>
          <Modal.Title>Review Stars</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs id="review-stars-edit-modal-tab" defaultActiveKey={1} className={styling.fieldLabel}>
            <Tab title="Facilities" eventKey={1}>
              <div className={styling.fieldLabel}>
                Select facilities to display star rating
              </div>
              <FacilitySelectTable
                starStyling={cx(styles.small, styles.primary)}
                enabledFacilities={enabledFacilities}
                liveFacilities={liveFacilities}
                handleSelectAllChange={this.handleSelectAllChange}
                handleChange={this.handleChange}
              />
            </Tab>
            <Tab title="Display" eventKey={2}>
              <div className={cx(styles.reviewContainer, backgroundClass)}>
                <Row className={cx(styles.textContainer, backgroundClass)}>
                  <div className={styles.stars}>
                    <StarRack
                      rating={rating}
                      className={cx(styles[starSize], styles[starColor])}
                    />
                  </div>
                  <Col className={cx(styles.ratingsText, backgroundClass, foregroundClass)}>
                    {reviewCount} Ratings
                  </Col>
                </Row>
              </div>
              <Row>
                <Col md={3}>
                  <div className={styling.fieldLabel}>Size</div>
                  <Select
                    autoBlur
                    className={styling.selectColorOptions}
                    clearable={false}
                    instanceId="star-size-selector"
                    onChange={this.selectStarSize}
                    optionRenderer={this.renderOption()}
                    valueRenderer={this.renderOption()}
                    options={starSizes}
                    value={starSize}
                    searchable={false}
                  />
                </Col>
                <Col md={3}>
                  <div className={styling.fieldLabel}>Star color</div>
                  <Select
                    autoBlur
                    className={styling.selectColorOptions}
                    clearable={false}
                    instanceId="star-color-selector"
                    onChange={this.selectStarColor}
                    optionRenderer={this.renderOption()}
                    valueRenderer={this.renderOption()}
                    options={starColors}
                    value={starColor}
                    searchable={false}
                  />
                </Col>
                <Col md={3}>
                  <div className={styling.fieldLabel}>Font color</div>
                  <Select
                    autoBlur
                    className={styling.selectColorOptions}
                    clearable={false}
                    instanceId="font-color-selector"
                    onChange={this.selectFontColor}
                    optionRenderer={this.renderColor('3px')}
                    valueRenderer={this.renderColor('10px')}
                    options={fontColors}
                    value={fontColor}
                    searchable={false}
                  />
                </Col>
                <Col md={3}>
                  <div className={styling.fieldLabel}>Background color</div>
                  <Select
                    autoBlur
                    className={styling.selectColorOptions}
                    clearable={false}
                    instanceId="bg-color-selector"
                    onChange={this.selectBackgroundColor}
                    optionRenderer={this.renderColor('3px')}
                    valueRenderer={this.renderColor('10px')}
                    options={bgColors}
                    value={backgroundColor}
                    searchable={false}
                  />
                </Col>
              </Row>
              <Row>
                <div className={styling.title}>
                  <div className={styling.fieldLabel}>Insert Link</div>
                  <Icon
                    icon="linkout2"
                    className={styling.icon}
                  />
                  <input
                    type="text"
                    name="redirect"
                    placeholder="/example-page"
                    color="#979797"
                    className={styling.redirectText}
                    value={redirect}
                    onChange={this.handleRedirectChange}
                  />
                </div>
              </Row>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="default" onClick={this.handleCancelEdit}>Cancel</Button>
          <Button
            onClick={this.save}
            variant="success"
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

ReviewStarsModal.propTypes = propTypes;
ReviewStarsModal.defaultProps = defaultProps;

ReviewStarsModal.fragments = {
  facility: gql`
    fragment ReviewStarsModalFacility on Facility {
      distanceFromCurrentUserInMiles
      pagePaths(filter: { live: $live, locale: $locale }) {
        path
        locale
      }
      ...FacilitySelectTableFacility
    }

    ${FacilitySelectTable.fragments.facility}
  `,
};

export default ReviewStarsModal;
